import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// mui import
import { CssBaseline, ThemeProvider } from "@mui/material";

// third party
import { SnackbarProvider } from "notistack";
import { useSelector } from "react-redux";

// project import
import { ColorModeContext, useMode } from "./theme";
import Calendar from "./scenes/calendar/calendar";
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import Dashboard from "./scenes/dashboard";
import Tenants from "./scenes/tenants";
import TenantForm from "./scenes/tenants/form";
import Users from "./scenes/users";
import UserForm from "./scenes/users/form";
import Assignments from "./scenes/assignments";
import AssignmentForm from "./scenes/assignments/form";
import Firms from "./scenes/firms";
import FirmForm from "./scenes/firms/form";
import Lookups from "./scenes/lookups";
import Form from "./scenes/form";
import FAQ from "./scenes/faq";
import Login from "./scenes/Auth/Login";
import InspectionSidebar from "./scenes/inspection/Sidebar";
import InspectionForm from "./scenes/inspection/form";
import InspectionReport from "./scenes/reports/inspectionreport";
import ObservationReport from "./scenes/reports/ObservationReport";
// import ObservationOrdering from "./scenes/reports/ObservationOrdering";
// import DiscussionOrdering from "./scenes/reports/DiscussionOrdering";
import ObservationReportGenerator from "./scenes/reports/ObservationReportGenerator";
import { COVERAGE_CHOICE_CITATION_GUID, COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID, IDLE_TIMEOUT_MINUTES, REPORT_TYPE } from "./utilities/constants";
import { setLogout } from "./state";
import CoverageDnD from "./scenes/reports/CoverageOrdering";
import AIv2 from "./scenes/aiv2";
import OneDriveDocuments from "./scenes/onedrivedocuments";
import Form483Extractions from "./scenes/aiv2/Form483Extractions";
import WarningLetterSummary from "./scenes/aiv2/WarningLetterSummary";
import CRLSummary from "./scenes/aiv2/CRLSummary";
import useIdleTimeout from "./hooks/useIdleTimeout";
import TimeoutPopup from "./components/TimeoutPopup";
import setupApiInterceptors from "./utilities/apiInterceptors";

function App() {
  const [theme, colorMode] = useMode();
  const dispatch = useDispatch();

  const isShowInspection = Boolean(useSelector((state) => {
    return state.isShowInspection;
  })); 

  const isAuthenticated = Boolean(useSelector((state) => {
    return state.isAuthenticated;
  }));

  useEffect(() => {
    // Setup interceptors for all API instances
    setupApiInterceptors(dispatch);
  }, [dispatch]);

  const [isSidebar, setIsSidebar] = useState(true);

  const handleLogout = () => {
    dispatch(setLogout());
    window.location.replace('/login');
  };

  const { showTimeoutPopup, setShowTimeoutPopup } = useIdleTimeout(handleLogout, isAuthenticated, IDLE_TIMEOUT_MINUTES);

  // Protected Route component
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider>
          <CssBaseline />
          <div className="app">
            <Routes>
              <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" replace />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <>
                      <Sidebar isSidebar={isSidebar} />
                      {isShowInspection && <InspectionSidebar isSidebar={isSidebar} />}
                      <main className="content">
                        <Topbar setIsSidebar={setIsSidebar} />
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/tenants" element={<Tenants />} /> 
                          <Route path="/tenants/create" element={<TenantForm />} />
                          <Route path="/tenants/:tenant_id/edit" element={<TenantForm />} />
                          <Route path="/users" element={<Users />} /> 
                          <Route path="/users/create" element={<UserForm />} />
                          <Route path="/firms" element={<Firms />} />
                          <Route path="/firms/create" element={<FirmForm />} />
                          <Route path="/firms/:firm_id/edit" element={<FirmForm />} />
                          <Route path="/lookup" element={<Lookups />} />
                          <Route path="/assignments/create" element={<AssignmentForm />} />
                          <Route path="/assignments/:assignment_uid/edit" element={<AssignmentForm />} />
                          <Route path="/assignments" element={<Assignments />} />
                          <Route path="/form" element={<Form />} />
                          <Route path="/calendar" element={<Calendar />} />
                          <Route path="/faq" element={<FAQ />} />
                          <Route path="/inspection/:inspectionUid/citationLanguageCode/:citationLanguageCode" element={<InspectionForm />} />
                          <Route path="/inspection/:inspectionUid/subPartCode/:subPartCode/citationLanguageCode/:citationLanguageCode/lookupcodetype/:lookupcodetype" element={<InspectionForm />} />
                          <Route path="/inspection/:inspectionUid/503b/new" element={<InspectionForm />} />
                          <Route path="/inspection/:inspectionUid/503b/:operationId" element={<InspectionForm />} />
                          <Route path="/inspection/:inspectionUid/503a/new" element={<InspectionForm />} />
                          <Route path="/inspection/:inspectionUid/503a/:operationId" element={<InspectionForm />} />
                          <Route path="/inspection/:inspectionUid/inspectionreport" element={<InspectionReport />} />
                          <Route path="/inspection/:inspectionUid/citationreport" element={<ObservationReport reportType={REPORT_TYPE.OBSERVATION}/>} />
                          <Route path="/inspection/:inspectionUid/observationordering" element={<CoverageDnD 
                            coverageDndType={REPORT_TYPE.OBSERVATION}
                            coverageChoiceGuid={COVERAGE_CHOICE_CITATION_GUID}
                            systemChoiceField="system_choice_observation" />} />
                          <Route path="/inspection/:inspectionUid/printcitation" element={<ObservationReportGenerator reportType={REPORT_TYPE.OBSERVATION} />}/>
                          <Route path="/inspection/:inspectionUid/discussionreport" element={<ObservationReport reportType={REPORT_TYPE.DISCUSSION}/>} />
                          <Route path="/inspection/:inspectionUid/discussionordering" element={<CoverageDnD 
                            coverageDndType={REPORT_TYPE.DISCUSSION}
                            coverageChoiceGuid={COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID}
                            systemChoiceField="system_choice_discussion" />} />
                          <Route path="/inspection/:inspectionUid/printdiscussion" element={<ObservationReportGenerator reportType={REPORT_TYPE.DISCUSSION}/>}/>
                          <Route path="/aidocument" element={<AIv2 />} />
                          <Route path="/onedrivedocuments" element={<OneDriveDocuments />} />
                          <Route path="/form483extractions" element={<Form483Extractions />} />
                          <Route path="/warninglettersummary" element={<WarningLetterSummary />} />
                          <Route path="/crlsummary" element={<CRLSummary />} />
                        </Routes>
                      </main>
                    </>
                  </ProtectedRoute>
                }
              />
            </Routes>
            <TimeoutPopup 
              open={showTimeoutPopup} 
              onClose={() => setShowTimeoutPopup(false)} 
            />
          </div>
        </SnackbarProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
